// src/components/exercise/AudioPlayer.tsx
import React, { useState } from 'react';
import { useAudio } from '../../hooks/useAudio';
import { useExerciseStore } from '../../stores/useExerciseStore';

const C = {
  surface: '#0d1220',
  card:    'rgba(255,255,255,0.04)',
  border:  'rgba(255,255,255,0.06)',
  accent:  '#4f8cf7',
  accent2: '#6366f1',
  success: '#34d399',
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
};

function fmt(secs: number): string {
  if (!secs || !isFinite(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const AudioPlayer: React.FC = () => {
  const currentUnitId  = useExerciseStore(s => s.currentUnitId);
  const unitMediaCache = useExerciseStore(s => s.unitMediaCache);
  const { isPlaying, currentTime, duration, seekTo, playPause, seekBackward, seekForward } = useAudio();

  const [seekHover, setSeekHover] = useState(false);

  const audioUrl = currentUnitId ? unitMediaCache[currentUnitId]?.audioUrl : undefined;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!audioUrl) {
    return (
      <div style={{
        padding: '14px 18px', borderRadius: '12px',
        border: '1px dashed rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.02)',
        color: C.muted, fontSize: '0.82rem', textAlign: 'center',
        letterSpacing: '0.04em',
      }}>
        No audio available for this unit
      </div>
    );
  }

  const handleSeekClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekTo(((e.clientX - rect.left) / rect.width) * 100);
  };

  return (
    <div style={{
      background:    'rgba(255,255,255,0.03)',
      border:        '1px solid rgba(255,255,255,0.06)',
      borderRadius:  '14px',
      padding:       '16px 20px',
      display:       'flex',
      flexDirection: 'column',
      gap:           '14px',
      backdropFilter:'blur(12px)',
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          fontSize: '0.68rem', fontWeight: 700, color: C.success,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
          </svg>
          Audio Listening
        </div>
        <div style={{ fontSize: '0.78rem', color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
          {fmt(currentTime)} / {fmt(duration)}
        </div>
      </div>

      {/* Seek bar */}
      <div
        style={{
          width: '100%', height: seekHover ? '6px' : '4px',
          background: 'rgba(255,255,255,0.08)', borderRadius: '99px',
          cursor: 'pointer', position: 'relative', transition: 'height 0.15s ease',
        }}
        onMouseEnter={() => setSeekHover(true)}
        onMouseLeave={() => setSeekHover(false)}
        onClick={handleSeekClick}
        title="Click to seek"
      >
        <div style={{
          position: 'absolute', top: 0, left: 0, height: '100%',
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${C.accent}, ${C.accent2})`,
          borderRadius: '99px', transition: 'width 0.25s linear',
        }} />
        {seekHover && duration > 0 && (
          <div style={{
            position: 'absolute', top: '50%', left: `${progress}%`,
            transform: 'translate(-50%, -50%)',
            width: '14px', height: '14px', borderRadius: '50%',
            background: C.accent,
            boxShadow: `0 0 0 3px rgba(79,140,247,0.25)`,
          }} />
        )}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <ControlBtn onClick={seekBackward} title="Seek back 5s">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="1 4 1 10 7 10"/>
            <path d="M3.51 15a9 9 0 1 0 .49-3.16"/>
            <text x="7" y="14" fontSize="6" fill="currentColor" stroke="none" fontWeight="bold">5</text>
          </svg>
        </ControlBtn>

        <button
          onClick={() => currentUnitId && playPause(currentUnitId, audioUrl)}
          style={{
            width: '48px', height: '48px', borderRadius: '50%', border: 'none',
            background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(79,140,247,0.4)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            fontSize: '1rem',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>

        <ControlBtn onClick={seekForward} title="Seek forward 5s">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-.49-3.16"/>
            <text x="7" y="14" fontSize="6" fill="currentColor" stroke="none" fontWeight="bold">5</text>
          </svg>
        </ControlBtn>
      </div>
    </div>
  );
};

const ControlBtn: React.FC<{ onClick: () => void; title: string; children: React.ReactNode }> = ({ onClick, title, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '36px', height: '36px', borderRadius: '50%',
        border: `1px solid ${hover ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
        background: hover ? 'rgba(255,255,255,0.06)' : 'transparent',
        color: hover ? C.text : C.muted,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', transition: 'all 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

export default AudioPlayer;
